/**
 * Supabase browser client.
 *
 * URL + anon key are *publishable* (anon key only — never the service role).
 * RLS in the database is what actually protects user data.
 *
 * Configure via Vite env vars in `.env.local` (gitignored):
 *   VITE_SUPABASE_URL=https://xxx.supabase.co
 *   VITE_SUPABASE_ANON_KEY=eyJhbGciOi...
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL || '';
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabaseConfigured = Boolean(url && anonKey);

// When Supabase isn't configured (early dev), expose a stub so the rest of the
// app doesn't crash on import. The auth context handles the unconfigured case
// by showing a banner and disabling auth-gated actions.
export const supabase: SupabaseClient = supabaseConfigured
  ? createClient(url, anonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : (createUnconfiguredStub() as unknown as SupabaseClient);

function createUnconfiguredStub() {
  const reject = () =>
    Promise.resolve({
      data: { session: null, user: null },
      error: { message: 'Supabase not configured — set VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY' },
    });
  return {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signInWithPassword: reject,
      signInWithOtp: reject,
      signUp: reject,
      signOut: () => Promise.resolve({ error: null }),
    },
  };
}
